import { defineStore } from 'pinia';
import { store } from '@/app/stores';
import { ModalState } from '@/app/types/modals';
import { type LocaleCode } from '@/app/lang/locales';
import type { I18n } from 'vue-i18n';

const LOCALE_STORAGE_KEY = 'app-locale';

let i18nInstance: I18n | null = null;

export function setI18nInstance(i18n: I18n) {
    i18nInstance = i18n;
}

interface SystemState {
    modalState: ModalState;
    isSlowNetwork: boolean;
    pingTime: number;
    locale: LocaleCode;
}

export const useSystemStore = defineStore('app-system', {
    state: (): SystemState => ({
        modalState: {
            show: false,
            title: '',
            message: '',
            confirmEvent: null,
        },
        isSlowNetwork: false,
        pingTime: 0,
        locale: (localStorage.getItem(LOCALE_STORAGE_KEY) as LocaleCode) || 'ko',
    }),
    getters: {
        getModalState(): ModalState {
            return this.modalState;
        },
        getIsSlowNetwork(): boolean {
            return this.isSlowNetwork;
        },
        getPingTime(): number {
            return this.pingTime;
        },
    },
    actions: {
        setIsSlowNetwork(isSlowNetwork: boolean) {
            this.isSlowNetwork = isSlowNetwork;
        },
        setPingTime(pingTime: number) {
            this.pingTime = pingTime;
        },
        setModalState(modalState: ModalState) {
            this.modalState = modalState;
        },
        hideModalState() {
            this.modalState.show = false;
            this.modalState.title = '';
            this.modalState.message = '';
            this.modalState.confirmEvent = null;
        },
        changeLocale(code: LocaleCode) {
            this.locale = code;
            localStorage.setItem(LOCALE_STORAGE_KEY, code);
            this.setLangClass();
            if (i18nInstance) {
                (i18nInstance.global.locale as any).value = code;
            }
        },
        setLangClass() {
            document.body.classList.remove('lang-ko', 'lang-en', 'lang-vi', 'lang-zh');
            document.body.classList.add(`lang-${this.locale}`);
        },
        hasTranslation() {
            return this.locale !== 'ko';
        },
    },
});

export function useSystemStoreWithOut() {
    return useSystemStore(store);
}
